<template>
  <div v-if="answers.columns.length" class="wrapper">
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th>{{ parseTranslation(COLUMNS) }}</th>
            <th></th>

            <th
              class="answer-column"
              v-for="(label, index) in answers.columns"
              :key="`${label}-${index}`"
            >
              <span>{{ reduceText(label) }}</span>

              <div class="tooltip">
                {{ label }}
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td></td>
            <td></td>
            <td
              v-for="(column, columnIndex) in answers.columns"
              :key="`${column}-input-filter`"
            >
              <input
                class="filter-input"
                type="text"
                :value="activeFilters[columnIndex]"
                @change="event => handleFilterChange(event, columnIndex)"
              />
            </td>
          </tr>

          <tr
            v-for="(answer, answerIndex) in filteredAnswers.slice(...paginationIndexes)"
            :key="`answer-${answerIndex}`"
          >
            <td>
              <input type="checkbox" />
            </td>

            <td>
              <button
                class="single-delete-btn"
                type="button"
                @click="deleteAnswer(answer)"
              >
                <TrashIcon />
              </button>
            </td>

            <td
              v-for="(answerValue, answerColumnIndex) in answer"
              :key="`answer-${answerIndex}-column-${answerColumnIndex}`"
            >
              <span>
                {{ answerValue }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="spacer"></div>

    <Pagination
      :items="filteredAnswers"
      :update_visible_items="updatePaginationIndexes"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import TrashIcon from '@/components/imgs/Trash.vue'

import Pagination from './Pagination.vue'

import * as translationAnswersTable from './AnswersTable.translations'

const { default: translations, ...translationKeys } = translationAnswersTable

const MAX_TEXT_LENGTH = 14

export default {
  components: {
    Pagination,

    TrashIcon,
  },

  props: {
    answers: {
      required: true,
      type: Object,
    },
    toggle_loading: {
      required: true,
      type: Function,
    },
  },

  data() {
    return {
      activeFilters: {},
      paginationIndexes: [],
      translations,
      ...translationKeys,
    }
  },

  computed: {
    filteredAnswers() {
      const hasAnyFilter = Object.values(this.activeFilters).some(Boolean)

      const allAnswers = this.answers.data

      if (!hasAnyFilter) return allAnswers

      return allAnswers.filter(answers => {
        const answerMatches = answers.every((answer, index) =>
          `${answer}`.toLowerCase().includes(this.activeFilters[index].toLowerCase()),
        )

        return answerMatches
      })
    },
  },

  methods: {
    ...mapActions(['executeSurveyAPICall', 'modalBtns', 'sendMessage']),

    reduceText(text) {
      if (text.length < MAX_TEXT_LENGTH) return text

      const reduced = text.substr(0, MAX_TEXT_LENGTH)

      return `${reduced}...`
    },

    buildFilters() {
      this.activeFilters = this.answers.columns.reduce(
        (acc, _, columnIndex) => ({
          ...acc,
          [columnIndex]: '',
        }),
        {},
      )
    },

    removeAnswer(answerId) {
      this.answers.data = this.answers.data.filter(([id]) => id !== answerId)
    },

    async executeAnswerDeletion({ answer_id, survey_id }) {
      this.toggle_loading()

      const deleted = await this.executeSurveyAPICall({
        path: 'survey/answers',
        method: 'DELETE',
        payload: { survey_id, answer_id },
      })

      this.toggle_loading()

      if (!deleted) return this.sendMessage('Could not delete answer')

      this.sendMessage('Answer deleted!')

      this.removeAnswer(answer_id)
    },

    deleteAnswer(answer) {
      /*
        In the current implementation (python), our backend sends data similarly to an CSV parsed data,
        in which we have an array of headers and an array of rows (also arrays).

        Currently, the first position of each row is the "answer_id" of each answer. This will be
        changed to be an array of objects, to better represent what they are.
      */
      const ANSWER_ID_INDEX = 0
      const answer_id = answer[ANSWER_ID_INDEX]

      this.modalBtns({
        text: `Are you sure you want to delete answer ${answer_id}?`,
        yes: this.executeAnswerDeletion.bind(this),
        params: { answer_id, survey_id: this.$route.query.survey },
      })
    },

    handleFilterChange(event, columnIndex) {
      // add debounce?
      const newValue = event.target.value

      this.activeFilters = {
        ...this.activeFilters,
        [columnIndex]: newValue || '',
      }
    },

    updatePaginationIndexes(paginationIndexes) {
      this.paginationIndexes = paginationIndexes
    },
  },

  created() {
    this.buildFilters()
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  margin: 20px 0;
}

.table-container {
  overflow-x: scroll;
  overflow-y: hidden;
  background: #f9f9f9;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.3), 0 1px 2px 0 rgba(0, 0, 0, 0.5);
  padding: 16px 0;
  border-radius: 4px;

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    width: 50px;
    background: #dcdcdc;
    padding: 2px;
  }

  &::-webkit-scrollbar {
    height: 10px;
    width: 12px;
  }

  .answer-column {
    position: relative;
  }

  .answer-column:hover .tooltip {
    display: block;
  }

  .tooltip {
    display: none;
    position: absolute;
    left: 0;
    top: calc(100% + 8px);
    color: $white;
    background: $blue;
    padding: 8px 16px;
    border-radius: 8px;
    left: 50%;
    transform: translateX(-50%);

    &:before {
      content: '';
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid $blue;
      /* same size as the border */
      top: -5px;

      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  th {
    border-bottom: 1px solid #000;
    text-align: center;
    vertical-align: bottom;
    min-width: 150px;
    padding: 16px 0 4px;
    text-transform: uppercase;
    color: #666;
    font-weight: 600;
    letter-spacing: 0.03em;
    font-size: 0.9rem;
  }

  tbody tr {
    border-bottom: 5px solid #fff;
  }

  td {
    padding: 16px 8px;
    text-align: center;
    vertical-align: middle;

    span {
      color: #666;
      font-size: 0.8rem;
      line-height: 1.4;
      text-align: center;
    }
  }

  .single-delete-btn {
    @include transparentButton;
    margin: 0 auto;
    color: $blue;
  }

  .filter-input {
    padding: 6px 12px;
    border-radius: 4px;
    border: 1px solid #ced4da;
    line-height: 1.5;
    font-size: 1rem;
    outline: none;
    color: #666;
    transition: border-color 0.22s ease;

    &:focus {
      border-color: $blue;
    }
  }
}

.spacer {
  margin: 20px 0;
}
</style>
